/* ==================================================================
 * DIALOGUE DATA
 *
 * Node-based. Each node has lines (revealed one tap at a time) and
 * either choices, a paid gate, or an end.
 *
 *   lines     array of strings, shown one at a time
 *   choices   [{ text, to, once }]  — `once` removes it after asking
 *   gate      { francs, price, to } — paid unlock
 *   requires  minimum number of `once` choices used before this option
 *             appears, so the player can't hit the paywall instantly
 *   sketch    filename stem in public/media/clues/ — shows a drawing
 *             above the dialogue while this node is playing
 *   end       true = conversation over, hand back to the app
 *
 * ------------------------------------------------------------------
 * CHARACTER #1 — THE TOWNSMAN
 *
 * WHAT HE KNOWS: street gossip, and nothing else.
 * WHAT HE BELIEVES: that Ghjaseppu found a pirate wreck. He is certain
 *   of this and he is probably wrong. It is a belief, not information.
 * WHAT HE DOES NOT KNOW: what Ghjaseppu actually did at the fort, what
 *   became of him, where he went, whether he is even dead. He does NOT
 *   know about the woman at the Citadel — the player finds the Citadel
 *   through the lockbox photo, not from him.
 *
 * His one real contribution: he has seen a box down past the old gate,
 * he drew it, and he will sell you the drawing.
 *
 * Keep him this ignorant. Every fact he gives away is a fact the
 * Citadel woman and the witch no longer get to reveal.
 * ================================================================== */

export const CHARACTERS = {
  townsman: {
    id: "townsman",
    /* No portrait asset yet — drop one in as
       public/media/characters/townsman.jpg when the footage exists. */
    portrait: "townsman.jpg",

    en: {
      name: "The Townsman",
      role: "Porto-Vecchio",
      start: "greeting",
      nodes: {
        greeting: {
          lines: ["You've been stood there a while.", "Waiting for someone?"],
          choices: [{ text: "Who is Ghjaseppu?", to: "whoIs" }],
        },

        whoIs: {
          lines: [
            "Ghjaseppu? Everyone knew Ghjaseppu.",
            "Had some position up at the fort. Don't ask me what — I never asked and he never said.",
            "What I know is he had money. More than a man in that job ought to have.",
          ],
          choices: [
            { text: "Where did his money come from?", to: "money", once: true },
            { text: "What happened to him?", to: "happened", once: true },
            { text: "Is the treasure real?", to: "treasure", once: true },
            { text: "I mean to go after it.", to: "serious", requires: 1 },
          ],
        },

        money: {
          lines: [
            "Nobody knows. That's the honest answer.",
            "But I'll tell you what I think.",
            "There's a wreck out in that water. Old one. Pirate ship, if you believe it — and I do.",
            "A man finds that, he doesn't need a position at the fort any more, does he.",
          ],
          choices: [
            { text: "What happened to him?", to: "happened", once: true },
            { text: "Is the treasure real?", to: "treasure", once: true },
            { text: "I mean to go after it.", to: "serious", requires: 1 },
          ],
        },

        happened: {
          lines: [
            "Couldn't tell you.",
            "He stopped coming around. That's the whole of it.",
            "Some say the maquis. Some say worse. People say a lot.",
            "For all I know he's sat somewhere warm right now, laughing at the lot of us.",
          ],
          choices: [
            { text: "Where did his money come from?", to: "money", once: true },
            { text: "Is the treasure real?", to: "treasure", once: true },
            { text: "I mean to go after it.", to: "serious", requires: 1 },
          ],
        },

        treasure: {
          lines: [
            "Real? I hear legends. I hear stories.",
            "But men have gone looking and come back with nothing. That much I've seen with my own eyes.",
            "Make of it what you like.",
          ],
          choices: [
            { text: "Where did his money come from?", to: "money", once: true },
            { text: "What happened to him?", to: "happened", once: true },
            { text: "I mean to go after it.", to: "serious", requires: 1 },
          ],
        },

        /* The push. Real practical warning, in his voice.
           Keep it accurate if the route changes. */
        serious: {
          lines: [
            "You're serious, then. You actually mean to go after it.",
            "Then hear me first.",
            "Half a day at the very least, and that's if nothing goes wrong.",
            "There's water to cross. People drown in that water, so don't be clever about it.",
            "Then the hills. Then scratching about in every corner looking for whatever he left.",
            "That takes time. And it takes money.",
            "So. Do you still want it?",
          ],
          choices: [
            { text: "Yes.", to: "knows" },
            { text: "And if it takes a lifetime?", to: "lifetime", once: true },
          ],
        },

        lifetime: {
          lines: [
            "Then it takes a lifetime. Some things do.",
            "You never know, do you.",
          ],
          choices: [{ text: "I still want it.", to: "knows" }],
        },

        knows: {
          lines: [
            "Well then.",
            "I do know a little. Not much. But a little.",
          ],
          choices: [{ text: "What do you know?", to: "offer" }],
        },

        offer: {
          lines: ["Ah. Now.", "Information has a price, my friend."],
          gate: { francs: 3, price: "€2.50", to: "clue" },
        },

        clue: {
          sketch: "lockbox",
          lines: [
            "There. Drew it myself, the other day.",
            "There's a box down past the old gate. Sitting where anyone could see it, if they knew to look.",
            "I couldn't open the thing. Locked, and not with a key.",
            "But whatever's inside tells you where to go next. I'd put money on it.",
            "Go on. Down toward the water.",
          ],
          end: true,
        },
      },
    },

    fr: {
      name: "L'homme du village",
      role: "Porto-Vecchio",
      start: "greeting",
      nodes: {
        greeting: {
          lines: [
            "Vous êtes planté là depuis un moment.",
            "Vous attendez quelqu'un ?",
          ],
          choices: [{ text: "Qui est Ghjaseppu ?", to: "whoIs" }],
        },

        whoIs: {
          lines: [
            "Ghjaseppu ? Tout le monde connaissait Ghjaseppu.",
            "Il avait un poste là-haut au fort. Ne me demandez pas lequel — je n'ai jamais demandé et il n'a jamais dit.",
            "Ce que je sais, c'est qu'il avait de l'argent. Plus qu'un homme dans ce poste ne devrait en avoir.",
          ],
          choices: [
            { text: "D'où venait son argent ?", to: "money", once: true },
            { text: "Que lui est-il arrivé ?", to: "happened", once: true },
            { text: "Le trésor existe vraiment ?", to: "treasure", once: true },
            { text: "J'ai l'intention d'aller le chercher.", to: "serious", requires: 1 },
          ],
        },

        money: {
          lines: [
            "Personne ne le sait. C'est la réponse honnête.",
            "Mais je vais vous dire ce que je pense, moi.",
            "Il y a une épave dans cette eau. Une vieille. Un navire pirate, si vous voulez y croire — et moi j'y crois.",
            "Un homme qui trouve ça n'a plus besoin d'un poste au fort, pas vrai ?",
          ],
          choices: [
            { text: "Que lui est-il arrivé ?", to: "happened", once: true },
            { text: "Le trésor existe vraiment ?", to: "treasure", once: true },
            { text: "J'ai l'intention d'aller le chercher.", to: "serious", requires: 1 },
          ],
        },

        happened: {
          lines: [
            "Je ne saurais vous dire.",
            "Il a cessé de venir. C'est tout.",
            "Certains disent le maquis. D'autres disent pire. Les gens disent beaucoup de choses.",
            "Pour ce que j'en sais, il est peut-être assis au chaud quelque part, en train de rire de nous tous.",
          ],
          choices: [
            { text: "D'où venait son argent ?", to: "money", once: true },
            { text: "Le trésor existe vraiment ?", to: "treasure", once: true },
            { text: "J'ai l'intention d'aller le chercher.", to: "serious", requires: 1 },
          ],
        },

        treasure: {
          lines: [
            "Vrai ? J'entends des légendes. J'entends des histoires.",
            "Mais des hommes sont partis le chercher et sont revenus les mains vides. Ça, je l'ai vu de mes yeux.",
            "Faites-en ce que vous voulez.",
          ],
          choices: [
            { text: "D'où venait son argent ?", to: "money", once: true },
            { text: "Que lui est-il arrivé ?", to: "happened", once: true },
            { text: "J'ai l'intention d'aller le chercher.", to: "serious", requires: 1 },
          ],
        },

        serious: {
          lines: [
            "Vous êtes sérieux, alors. Vous comptez vraiment y aller.",
            "Alors écoutez-moi d'abord.",
            "Une demi-journée au bas mot, et encore, si rien ne tourne mal.",
            "Il y a de l'eau à traverser. On se noie dans cette eau, alors ne faites pas le malin.",
            "Ensuite les hauteurs. Ensuite fouiller chaque recoin à la recherche de ce qu'il a laissé.",
            "Ça prend du temps. Et ça coûte de l'argent.",
            "Alors. Vous le voulez toujours ?",
          ],
          choices: [
            { text: "Oui.", to: "knows" },
            { text: "Et si ça prend une vie entière ?", to: "lifetime", once: true },
          ],
        },

        lifetime: {
          lines: [
            "Alors ça prendra une vie entière. Certaines choses sont comme ça.",
            "On ne sait jamais, pas vrai.",
          ],
          choices: [{ text: "Je le veux quand même.", to: "knows" }],
        },

        knows: {
          lines: [
            "Bon, alors.",
            "Je sais un peu de choses. Pas grand-chose. Mais un peu.",
          ],
          choices: [{ text: "Que savez-vous ?", to: "offer" }],
        },

        offer: {
          lines: ["Ah. Voilà.", "Les informations ont un prix, mon ami."],
          gate: { francs: 3, price: "2,50 €", to: "clue" },
        },

        clue: {
          sketch: "lockbox",
          lines: [
            "Tenez. Je l'ai dessiné moi-même, l'autre jour.",
            "Il y a une boîte là-bas, après la vieille porte. Posée là où n'importe qui la verrait, s'il savait où regarder.",
            "Je n'ai pas réussi à l'ouvrir. Fermée, et pas avec une clé.",
            "Mais ce qu'il y a dedans vous dira où aller ensuite. J'y mettrais de l'argent.",
            "Allez. Descendez vers l'eau.",
          ],
          end: true,
        },
      },
    },

    it: {
      name: "Il paesano",
      role: "Porto-Vecchio",
      start: "greeting",
      nodes: {
        greeting: {
          lines: ["Sei fermo lì da un po'.", "Aspetti qualcuno?"],
          choices: [{ text: "Chi è Ghjaseppu?", to: "whoIs" }],
        },

        whoIs: {
          lines: [
            "Ghjaseppu? Lo conoscevano tutti.",
            "Aveva un posto su al forte. Non chiedermi quale — io non ho mai chiesto e lui non l'ha mai detto.",
            "Quello che so è che aveva soldi. Più di quanti un uomo in quel posto dovrebbe averne.",
          ],
          choices: [
            { text: "Da dove venivano i suoi soldi?", to: "money", once: true },
            { text: "Cosa gli è successo?", to: "happened", once: true },
            { text: "Il tesoro esiste davvero?", to: "treasure", once: true },
            { text: "Ho intenzione di cercarlo.", to: "serious", requires: 1 },
          ],
        },

        money: {
          lines: [
            "Nessuno lo sa. Questa è la risposta onesta.",
            "Ma ti dico quello che penso io.",
            "C'è un relitto là fuori in quell'acqua. Vecchio. Una nave pirata, se ci vuoi credere — e io ci credo.",
            "Un uomo che trova una cosa così non ha più bisogno di un posto al forte, no?",
          ],
          choices: [
            { text: "Cosa gli è successo?", to: "happened", once: true },
            { text: "Il tesoro esiste davvero?", to: "treasure", once: true },
            { text: "Ho intenzione di cercarlo.", to: "serious", requires: 1 },
          ],
        },

        happened: {
          lines: [
            "Non saprei dirti.",
            "Ha smesso di farsi vedere. Tutto qui.",
            "Alcuni dicono la macchia. Altri dicono di peggio. La gente dice tante cose.",
            "Per quanto ne so è seduto da qualche parte al caldo proprio adesso, a ridere di tutti noi.",
          ],
          choices: [
            { text: "Da dove venivano i suoi soldi?", to: "money", once: true },
            { text: "Il tesoro esiste davvero?", to: "treasure", once: true },
            { text: "Ho intenzione di cercarlo.", to: "serious", requires: 1 },
          ],
        },

        treasure: {
          lines: [
            "Vero? Sento leggende. Sento storie.",
            "Ma degli uomini sono andati a cercarlo e sono tornati a mani vuote. Quello l'ho visto con i miei occhi.",
            "Fanne quello che vuoi.",
          ],
          choices: [
            { text: "Da dove venivano i suoi soldi?", to: "money", once: true },
            { text: "Cosa gli è successo?", to: "happened", once: true },
            { text: "Ho intenzione di cercarlo.", to: "serious", requires: 1 },
          ],
        },

        serious: {
          lines: [
            "Allora fai sul serio. Hai davvero intenzione di andarci.",
            "Allora ascoltami prima.",
            "Mezza giornata come minimo, e questo se non va storto niente.",
            "C'è acqua da attraversare. In quell'acqua la gente annega, quindi non fare il furbo.",
            "Poi le colline. Poi frugare in ogni angolo cercando quello che ha lasciato.",
            "Ci vuole tempo. E ci vogliono soldi.",
            "Allora. Lo vuoi ancora?",
          ],
          choices: [
            { text: "Sì.", to: "knows" },
            { text: "E se ci vuole una vita?", to: "lifetime", once: true },
          ],
        },

        lifetime: {
          lines: [
            "Allora ci vorrà una vita. Certe cose vanno così.",
            "Non si sa mai, no?",
          ],
          choices: [{ text: "Lo voglio lo stesso.", to: "knows" }],
        },

        knows: {
          lines: ["E va bene.", "Qualcosa la so. Non molto. Ma qualcosa."],
          choices: [{ text: "Cosa sai?", to: "offer" }],
        },

        offer: {
          lines: ["Ah. Ecco.", "Le informazioni hanno un prezzo, amico mio."],
          gate: { francs: 3, price: "2,50 €", to: "clue" },
        },

        clue: {
          sketch: "lockbox",
          lines: [
            "Ecco. L'ho disegnato io, l'altro giorno.",
            "C'è una scatola laggiù, oltre la vecchia porta. Messa dove chiunque la vedrebbe, se sapesse dove guardare.",
            "Non sono riuscito ad aprirla. Chiusa, e non con una chiave.",
            "Ma quello che c'è dentro ti dice dove andare dopo. Ci scommetterei.",
            "Vai. Giù verso l'acqua.",
          ],
          end: true,
        },
      },
    },
  },
};
